/**
 * Public page meta — single source of truth for every indexable marketing route (PBI #1270).
 *
 * Consumed by three layers that must never disagree:
 * 1. Runtime `usePageMeta` (document.title, meta description, canonical) via `landingContent.meta`.
 * 2. Build-time crawl HTML (`plugins/publicCrawlSnippets.ts` -> `plugins/publicCrawlHtml.ts`),
 *    which rewrites title / description / canonical / og:* / twitter:* per route for
 *    crawlers and social unfurlers that do not execute JavaScript.
 * 3. The static `index.html` (homepage entry) — guarded by `src/test/publicPageMeta.test.ts`.
 *
 * Business rules:
 * - Titles are benefit-first and at most PUBLIC_TITLE_MAX_LENGTH characters; descriptions
 *   at most PUBLIC_DESCRIPTION_MAX_LENGTH, so search snippets are not truncated on mobile.
 * - No dollar amounts or plan prices here — pricing lives in AppSettings and is rendered at
 *   runtime (see `marketingPricingCopy.ts`).
 * - The homepage title is chosen once and left alone to avoid title churn in Google.
 * - This module is imported by the eager landing bundle: keep it to short strings only.
 *   Crawl HTML bodies belong in the build-only `plugins/publicCrawlSnippets.ts`.
 */

import { PUBLIC_ROUTES } from '../constants/routes';
import { AI_PUBLIC_ROUTES } from '../constants/aiPublicRoutes';

/** Public product name for the automation feature (two words — matches UI copy). */
export const AUTO_SEND_PUBLIC_NAME = 'Auto Send';

/**
 * Answer-first definition models can quote for job-intent queries.
 * Avoid dollar amounts; entitlements live in FAQ builders at runtime.
 */
export const AUTO_SEND_DEFINITION =
  'Auto Send is Scheduled Smiles\u2019 feature that automatically delivers e-cards, mailed cards, or flowers on birthdays, anniversaries, and other dates you choose \u2014 with a pre-send reminder so you can review, edit, or cancel before anything goes out.';

/** Shared Auto Send definition for crawl HTML / FAQ (not shown as a second hero paragraph). */
export const AUTO_SEND_LANDING_LEAD = AUTO_SEND_DEFINITION;

/** Brand suffix appended to most public titles. */
export const PUBLIC_TITLE_BRAND_SUFFIX = ' | Scheduled Smiles';

/** Google shows roughly 60 title characters on mobile before truncating. */
export const PUBLIC_TITLE_MAX_LENGTH = 60;

/** Google shows roughly 155 description characters on mobile before truncating. */
export const PUBLIC_DESCRIPTION_MAX_LENGTH = 155;

/**
 * Social (og:description / twitter:description) copy for the homepage card.
 * Conversational rather than keyword-stuffed — shown in feeds, not search results.
 */
export const PUBLIC_SOCIAL_DESCRIPTION =
  'Never forget a birthday or anniversary again. Auto Send delivers e-cards, mailed greeting cards, and flowers on the right day \u2014 free to start.';

/** One-sentence product summary shared by WebApplication JSON-LD and llms.txt. */
export const PUBLIC_PRODUCT_SUMMARY =
  'Scheduled Smiles is the one-stop shop for recurring gifting: schedule free e-cards, mailed greeting cards, and flowers once, and Auto Send delivers them on birthdays, anniversaries, and other dates you choose \u2014 with a pre-send reminder so you stay in control.';

/** Organization JSON-LD description. */
export const PUBLIC_ORGANIZATION_DESCRIPTION =
  'Scheduled Smiles builds tools that automatically send e-cards, mailed greeting cards, and flowers for birthdays, anniversaries, work milestones, and custom occasions.';

/** HowTo JSON-LD name and description (mainEntityOfPage is /how-it-works). */
export const PUBLIC_HOWTO_NAME =
  'How to automatically send birthday and anniversary e-cards, mailed cards, and flowers with Scheduled Smiles';
export const PUBLIC_HOWTO_DESCRIPTION =
  'Set up Auto Send in three steps. Scheduled Smiles delivers the e-card, mailed card, or flowers on the occasion so you never forget an important date.';

/** Title, description, and canonical path for one indexable public route. */
export interface PublicPageMeta {
  title: string;
  description: string;
  canonicalPath: string;
}

/**
 * Meta for every URL in `sitemap-pages.xml`. Keys are stable identifiers used by
 * `landingContent.meta` and page components; `canonicalPath` is the sitemap URL path.
 */
export const PUBLIC_PAGE_META = {
  home: {
    title: `Automate Birthday Wishes & Occasion Gifts${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Your one-stop shop for recurring gifting: Auto Send delivers free e-cards, mailed greeting cards, and flowers on the right day, every time.',
    canonicalPath: PUBLIC_ROUTES.home,
  },
  cards: {
    title: `Free Birthday & Anniversary E-Cards${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Browse free birthday and anniversary e-cards. Personalize with your own photos and message, then schedule delivery for exactly the right day.',
    canonicalPath: PUBLIC_ROUTES.cards,
  },
  flowers: {
    title: `Schedule Flower Delivery in Advance${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Browse flower arrangements for birthdays, anniversaries, and every occasion. Schedule delivery from local florists, timed to the day.',
    canonicalPath: PUBLIC_ROUTES.flowers,
  },
  mailedCards: {
    title: `Mailed Greeting Cards, Scheduled & Posted${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Browse physical greeting cards for birthdays, anniversaries, and milestones. Personalize the message and we print and mail it on time.',
    canonicalPath: PUBLIC_ROUTES.mailedCards,
  },
  gifts: {
    title: `Curated Gifts, Scheduled & Shipped${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Browse admin-approved physical gifts for birthdays and milestones. Schedule delivery; we ship through Goody when the occasion is near.',
    canonicalPath: PUBLIC_ROUTES.gifts,
  },
  howItWorks: {
    title: 'How Auto Send Works \u2014 E-Cards, Mailed Cards & Flowers',
    description:
      'Add contacts, pick an e-card, mailed card, or flowers, and turn on Auto Send. Scheduled Smiles delivers on the occasion with a pre-send reminder.',
    canonicalPath: PUBLIC_ROUTES.howItWorks,
  },
  pricing: {
    title: `Pricing: Free E-Cards & Personal Plan${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Compare Free and Personal. Free e-cards to start. Personal adds contact import, Auto Send, unlimited contacts, and member pricing.',
    canonicalPath: PUBLIC_ROUTES.pricing,
  },
  faq: {
    title: 'Scheduled Smiles FAQ \u2014 Auto Send, E-Cards & Flowers',
    description:
      'Answers about Auto Send, pricing, e-cards, mailed cards, flower delivery, privacy, and scheduling occasion gifts with Scheduled Smiles.',
    canonicalPath: PUBLIC_ROUTES.faq,
  },
  guidesIndex: {
    title: `Occasion Gift Guides${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Guides for scheduling birthday and anniversary gifts with Auto Send \u2014 e-cards, mailed cards, and flowers delivered on the right day.',
    canonicalPath: PUBLIC_ROUTES.guides,
  },
  automatedOccasionGiftsGuide: {
    title: `Auto Send Birthday Flowers & Cards${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'How to automatically send birthday flowers, e-cards, or mailed cards on the right day. Auto Send vs calendar reminders \u2014 and when to use each.',
    canonicalPath: PUBLIC_ROUTES.automatedOccasionGiftsGuide,
  },
  emailDeliveryHelp: {
    title: `Help Your Emails Arrive${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Steps for Gmail, Outlook, Yahoo, and Apple Mail to keep Scheduled Smiles occasion reminders and e-cards out of spam.',
    canonicalPath: PUBLIC_ROUTES.emailDeliveryHelp,
  },
  terms: {
    title: `Terms of Service${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Terms of use for Scheduled Smiles, the one-stop shop for scheduling e-cards, mailed greeting cards, and flowers with Auto Send.',
    canonicalPath: PUBLIC_ROUTES.terms,
  },
  privacy: {
    title: `Privacy Policy${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'How Scheduled Smiles handles your data and your contacts\u2019 details. Privacy-first: never shared, sold, or used for marketing.',
    canonicalPath: PUBLIC_ROUTES.privacy,
  },
  aiHub: {
    title: `Send Gifts From Your Assistant${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Add Scheduled Smiles to the assistant you already use. Ask for an e-card, mailed card, or flowers in chat, then approve before anything is sent.',
    canonicalPath: AI_PUBLIC_ROUTES.hub,
  },
  aiUseCaseCatalog: {
    title: `Assistant Catalog & Planning${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Use public MCP tools to browse e-cards, mailed cards, flowers, plans, and order-by deadlines before you sign in.',
    canonicalPath: AI_PUBLIC_ROUTES.useCaseCatalog,
  },
  aiUseCaseScheduling: {
    title: `Schedule Smiles From Chat${PUBLIC_TITLE_BRAND_SUFFIX}`,
    description:
      'Draft and approve e-cards, flowers, and mailed cards through a connected assistant — same review and payment rules as the website.',
    canonicalPath: AI_PUBLIC_ROUTES.useCaseScheduling,
  },
} as const satisfies Record<string, PublicPageMeta>;

/**
 * Short labels for breadcrumb trails (PageBreadcrumbs). Kept next to the meta so the
 * visible trail, BreadcrumbList JSON-LD, and canonical paths share one vocabulary.
 */
export const PUBLIC_BREADCRUMB_LABELS = {
  home: 'Home',
  flowers: 'Flowers',
  mailedCards: 'Mailed cards',
  gifts: 'Gifts',
} as const;

/** Stable key type for `PUBLIC_PAGE_META`. */
export type PublicPageMetaKey = keyof typeof PUBLIC_PAGE_META;

/**
 * Same meta keyed by canonical URL path (used by the build-time crawl HTML plugin and tests).
 * A function rather than a module-level constant so Rollup can drop it from the app bundle.
 */
export function getPublicPageMetaByPath(): Readonly<Record<string, PublicPageMeta>> {
  return Object.fromEntries(
    Object.values(PUBLIC_PAGE_META).map((meta) => [meta.canonicalPath, meta]),
  );
}

/** Every indexable marketing path, in `PUBLIC_PAGE_META` order (build plugins and tests). */
export function getPublicPagePaths(): readonly string[] {
  return Object.values(PUBLIC_PAGE_META).map((meta) => meta.canonicalPath);
}
